import { useState } from "react";
import {
  FindingsFilter,
  FindingsOrderField,
  FindingType,
  GetAffectedAssetsQuery,
  GetAffectedAssetsQueryResult,
  Maybe,
  OrderDirection,
  ScoreStateFilter,
  useGetAffectedAssetsQuery,
} from "~/operations";

const commonOrderBy = {
  field: FindingsOrderField.RiskValue,
  direction: OrderDirection.Desc,
};

type FindingsConnection = Extract<
  GetAffectedAssetsQuery["findings"],
  { __typename: "FindingsConnection" }
>;

type FindingsConnectionEdge = NonNullable<FindingsConnection["edges"]>;
export type FindingsEdge = FindingsConnectionEdge[0];

type UseFetchAllFindingsProps = {
  scopeMrn: string;
  filter: Partial<FindingsFilter>;
  skip?: boolean;
};

export const useCheckReportsFetchAllFindings = ({
  scopeMrn,
  filter,
  skip,
}: UseFetchAllFindingsProps) => {
  const [allFindings, setAllFindings] = useState<FindingsEdge[]>([]);
  const pageSize = 100;

  const { fetchMore, networkStatus: allFindingsNetworkStatus } =
    useGetAffectedAssetsQuery({
      variables: {
        scopeMrn,
        first: 100,
        orderBy: commonOrderBy,
        filter,
      },
      notifyOnNetworkStatusChange: true,
      onCompleted: (data: GetAffectedAssetsQueryResult["data"]) => {
        handleNewData(data);
      },
      skip,
    });

  const handleNewData = async (data: GetAffectedAssetsQueryResult["data"]) => {
    if (!data || data.findings?.__typename !== "FindingsConnection") return;

    const newEdges = data.findings.edges;
    const hasNextPage = data.findings.pageInfo.hasNextPage;

    setAllFindings((prev) => [...(prev ?? []), ...(newEdges ?? [])]);

    if (!hasNextPage) return;

    const after = data.findings.pageInfo.endCursor;
    await fetchMore({
      variables: {
        first: pageSize,
        after,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return fetchMoreResult;
      },
    });
  };

  return { allFindings, allFindingsNetworkStatus };
};
